import { useCallback, useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import {
  API_ROUTES,
  KOLO_DEFAULT_SETTINGS,
  KOLO_MAX_OPTIONS,
  KOLO_MIN_OPTIONS,
  koloExtraTurnCount,
  koloSpinDurationMs,
  parseKoloLine,
  parseKoloOptions,
  planKoloSpin,
  type KoloSettings,
  type KoloSpin,
} from "@ziolo/shared";
import { Button, Card } from "@ziolo/ui";
import { apiRequest, errorMessage } from "../lib/apiClient.js";
import { formatTimestamp } from "../lib/format.js";
import { useSession } from "../session/SessionProvider.js";
import { KoloWheel } from "../games/kolo/KoloWheel.js";
import { playKoloSpinSound, playKoloWinSound } from "../games/kolo/koloSound.js";
import { ADMIN_LIST, AdminDeleteButton, useAdminListTools } from "../components/admin/AdminChrome.js";
import "../styles/kolo.css";

const OPTIONS_KEY = "ziolo.kolo.options";
const SETTINGS_KEY = "ziolo.kolo.settings";

function loadStoredOptions(): string[] {
  try {
    const raw = localStorage.getItem(OPTIONS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item): item is string => typeof item === "string")
      .map((item) => parseKoloLine(item))
      .filter((item): item is string => Boolean(item))
      .slice(0, KOLO_MAX_OPTIONS);
  } catch {
    return [];
  }
}

function persistOptions(options: string[]) {
  try {
    localStorage.setItem(OPTIONS_KEY, JSON.stringify(options));
  } catch {
    // Private mode or quota — the wheel still works this session.
  }
}

function loadSettings(): KoloSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return KOLO_DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<KoloSettings>;
    return {
      speed:
        parsed.speed === "short" || parsed.speed === "long" || parsed.speed === "normal"
          ? parsed.speed
          : KOLO_DEFAULT_SETTINGS.speed,
      extraTurns:
        parsed.extraTurns === "few" || parsed.extraTurns === "many" || parsed.extraTurns === "normal"
          ? parsed.extraTurns
          : KOLO_DEFAULT_SETTINGS.extraTurns,
      sound: typeof parsed.sound === "boolean" ? parsed.sound : KOLO_DEFAULT_SETTINGS.sound,
      autoRemoveWinner:
        typeof parsed.autoRemoveWinner === "boolean"
          ? parsed.autoRemoveWinner
          : KOLO_DEFAULT_SETTINGS.autoRemoveWinner,
      palette: parsed.palette === "vivid" || parsed.palette === "site" ? parsed.palette : KOLO_DEFAULT_SETTINGS.palette,
    };
  } catch {
    return KOLO_DEFAULT_SETTINGS;
  }
}

export function KoloPage() {
  const { user } = useSession();
  const [options, setOptions] = useState<string[]>(loadStoredOptions);
  const [settings, setSettings] = useState<KoloSettings>(loadSettings);
  const [draft, setDraft] = useState("");
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [winner, setWinner] = useState<string | null>(null);
  const [history, setHistory] = useState<KoloSpin[]>([]);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const rotationRef = useRef(0);
  const spinFrame = useRef(0);
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  useEffect(() => {
    persistOptions(options);
  }, [options]);

  useEffect(() => {
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    } catch {
      // Same as options: local-only prefs.
    }
  }, [settings]);

  const reloadHistory = useCallback(async () => {
    try {
      const next = await apiRequest<KoloSpin[]>(API_ROUTES.kolo.spins);
      setHistory(next);
      setHistoryError(null);
    } catch (cause) {
      setHistoryError(errorMessage(cause));
    }
  }, []);

  const admin = useAdminListTools({
    ...ADMIN_LIST.kolo,
    onReload: reloadHistory,
  });

  useEffect(() => {
    void reloadHistory();
    const id = window.setInterval(() => void reloadHistory(), 8000);
    return () => {
      window.clearInterval(id);
      cancelAnimationFrame(spinFrame.current);
    };
  }, [reloadHistory]);

  const addFromDraft = useCallback(() => {
    const incoming =
      draft.includes(",") || draft.includes(";") || draft.includes("\n")
        ? parseKoloOptions(draft)
        : (() => {
            const one = parseKoloLine(draft);
            return one ? [one] : [];
          })();
    if (incoming.length === 0) return;
    setOptions((current) => [...current, ...incoming].slice(0, KOLO_MAX_OPTIONS));
    setDraft("");
  }, [draft]);

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    addFromDraft();
  }

  function onDraftKey(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      event.preventDefault();
      addFromDraft();
    }
  }

  function removeAt(index: number) {
    if (spinning) return;
    setOptions((current) => current.filter((_, i) => i !== index));
  }

  function clearAll() {
    if (spinning) return;
    setOptions([]);
    setWinner(null);
    setRotation(0);
    rotationRef.current = 0;
  }

  function patchSettings<K extends keyof KoloSettings>(key: K, value: KoloSettings[K]) {
    setSettings((current) => ({ ...current, [key]: value }));
  }

  async function publishSpin(name: string, wheel: string[]) {
    try {
      await apiRequest<KoloSpin>(API_ROUTES.kolo.spins, {
        method: "POST",
        body: { winner: name, options: wheel },
      });
      await reloadHistory();
    } catch (cause) {
      setHistoryError(errorMessage(cause));
    }
  }

  function finishSpin(name: string, wheel: string[]) {
    setWinner(name);
    if (settingsRef.current.sound) playKoloWinSound();
    if (settingsRef.current.autoRemoveWinner) {
      const index = wheel.indexOf(name);
      setOptions((current) =>
        index === -1 ? current : current.filter((_, i) => i !== index),
      );
    }
    if (user) void publishSpin(name, wheel);
  }

  function spin() {
    const wheel = optionsRef.current;
    if (spinning || wheel.length < KOLO_MIN_OPTIONS) return;
    const prefs = settingsRef.current;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const plan = planKoloSpin(
      wheel.length,
      rotationRef.current,
      koloExtraTurnCount(prefs.extraTurns),
    );
    if (prefs.sound) playKoloSpinSound();

    if (reduced) {
      rotationRef.current = plan.targetAngle;
      setRotation(plan.targetAngle);
      const name = wheel[plan.index] ?? "";
      finishSpin(name, wheel);
      return;
    }

    setSpinning(true);
    setWinner(null);
    const start = rotationRef.current;
    const delta = plan.targetAngle - start;
    const started = performance.now();
    const duration = koloSpinDurationMs(prefs.speed);

    const tick = (now: number) => {
      const t = Math.min(1, (now - started) / duration);
      const eased = 1 - (1 - t) ** 3;
      const next = start + delta * eased;
      rotationRef.current = next;
      setRotation(next);
      if (t < 1) {
        spinFrame.current = requestAnimationFrame(tick);
        return;
      }
      rotationRef.current = plan.targetAngle;
      setRotation(plan.targetAngle);
      setSpinning(false);
      const name = wheel[plan.index] ?? "";
      finishSpin(name, wheel);
    };
    spinFrame.current = requestAnimationFrame(tick);
  }

  function dropWinner() {
    if (!winner || spinning) return;
    const index = options.indexOf(winner);
    setOptions((current) =>
      index === -1 ? current : current.filter((_, i) => i !== index),
    );
    setWinner(null);
  }

  const canSpin = options.length >= KOLO_MIN_OPTIONS && !spinning;
  const atCap = options.length >= KOLO_MAX_OPTIONS;

  return (
    <div className="kolo">
      <section className="kolo__stage">
        <header className="kolo__header">
          <h2 className="kolo__title">
            <i className="z-icon" aria-hidden="true">
              🎡
            </i>
            Koło fortuny
          </h2>
        </header>
        <KoloWheel options={options} rotation={rotation} palette={settings.palette} />
      </section>

      <aside className="kolo__side">
        <Card variant="panel" title="Opcje" icon="✏️">
          <form className="kolo-add" onSubmit={onSubmit}>
            <input
              className="kolo-add__input"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={onDraftKey}
              placeholder="np. kebab, chińczyk, nic"
              maxLength={80}
              disabled={spinning || atCap}
              aria-label="Nowa opcja"
            />
            <Button type="submit" variant="primary" disabled={spinning || atCap || !draft.trim()}>
              Dodaj
            </Button>
          </form>
          <p className="hub-hint">
            Enter dodaje. Wklej listę rozdzieloną przecinkami albo enterami. Maks.{" "}
            {KOLO_MAX_OPTIONS}.
          </p>

          {options.length === 0 ? (
            <p className="hub-hint">Pusto. Dodaj przynajmniej dwie opcje.</p>
          ) : (
            <ul className="kolo-chips">
              {options.map((option, index) => (
                <li key={`${option}-${index}`}>
                  <button
                    type="button"
                    className="kolo-chip"
                    disabled={spinning}
                    onClick={() => removeAt(index)}
                    aria-label={`Usuń ${option}`}
                  >
                    {option}
                    <span aria-hidden="true">×</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="kolo-actions">
            <Button variant="primary" disabled={!canSpin} onClick={spin}>
              {spinning ? "Kręci się..." : "Zakręć"}
            </Button>
            <Button disabled={spinning || options.length === 0} onClick={clearAll}>
              Wyczyść
            </Button>
          </div>
        </Card>

        <Card variant="panel" title="Ustawienia" icon="⚙️">
          <label className="kolo-setting">
            <span>Czas kręcenia</span>
            <select
              value={settings.speed}
              disabled={spinning}
              onChange={(event) => patchSettings("speed", event.target.value as KoloSettings["speed"])}
            >
              <option value="short">Krótki</option>
              <option value="normal">Normalny</option>
              <option value="long">Długi</option>
            </select>
          </label>
          <label className="kolo-setting">
            <span>Obroty</span>
            <select
              value={settings.extraTurns}
              disabled={spinning}
              onChange={(event) =>
                patchSettings("extraTurns", event.target.value as KoloSettings["extraTurns"])
              }
            >
              <option value="few">Mało</option>
              <option value="normal">Normalnie</option>
              <option value="many">Dużo</option>
            </select>
          </label>
          <label className="kolo-setting">
            <span>Kolory</span>
            <select
              value={settings.palette}
              disabled={spinning}
              onChange={(event) =>
                patchSettings("palette", event.target.value as KoloSettings["palette"])
              }
            >
              <option value="site">zioło.eu</option>
              <option value="vivid">Kolorowe</option>
            </select>
          </label>
          <label className="kolo-check">
            <input
              type="checkbox"
              checked={settings.sound}
              disabled={spinning}
              onChange={(event) => patchSettings("sound", event.target.checked)}
            />
            Dźwięk
          </label>
          <label className="kolo-check">
            <input
              type="checkbox"
              checked={settings.autoRemoveWinner}
              disabled={spinning}
              onChange={(event) => patchSettings("autoRemoveWinner", event.target.checked)}
            />
            Wyrzucaj wylosowane z koła
          </label>
        </Card>

        <Card variant="panel" title="Ostatnie losowania" icon="📝">
          {admin.isAdmin ? (
            <div className="admin-onpage__toolbar">
              <Button size="sm" variant="danger" disabled={admin.busy} onClick={() => void admin.wipe()}>
                Wyczyść historię
              </Button>
            </div>
          ) : null}
          {historyError ? <p className="hub-error">{historyError}</p> : null}
          {history.length === 0 && !historyError ? (
            <p className="hub-hint">Jeszcze nikt nie zakręcił. Wspólna lista dla wszystkich.</p>
          ) : (
            <ol className="kolo-history">
              {history.map((spin) => (
                <li key={spin.id} className="kolo-history__row">
                  <span className="kolo-history__meta">
                    {spin.username} · {formatTimestamp(spin.createdAt)}
                  </span>
                  <p className="kolo-history__options">
                    {spin.options.map((option, index) => (
                      <span key={`${spin.id}-${index}`}>
                        {index > 0 ? " · " : null}
                        {option === spin.winner ? (
                          <mark className="kolo-history__winner">{option}</mark>
                        ) : (
                          option
                        )}
                      </span>
                    ))}
                  </p>
                  {admin.isAdmin ? (
                    <div className="kolo-history__admin">
                      <AdminDeleteButton
                        confirm="Usunąć to losowanie?"
                        disabled={admin.busy}
                        onConfirm={() => admin.remove(spin.id)}
                      />
                    </div>
                  ) : null}
                </li>
              ))}
            </ol>
          )}
        </Card>
      </aside>

      {winner && !spinning ? (
        <div className="kolo-overlay" role="dialog" aria-modal="true" aria-labelledby="kolo-win-title">
          <div className="kolo-overlay__panel">
            <h2 id="kolo-win-title">Wypadło</h2>
            <p className="kolo-overlay__pick">{winner}</p>
            <div className="kolo-overlay__actions">
              <Button variant="primary" onClick={() => setWinner(null)}>
                OK
              </Button>
              {!settings.autoRemoveWinner && options.length > KOLO_MIN_OPTIONS ? (
                <Button onClick={dropWinner}>Wyrzuć z koła</Button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
